/** Devices tab (#310): ONE table where a row is a MAC — live devices, static
 *  addresses and blocked devices together, filtered by chips that live in the
 *  query string so every view is addressable and every filter is in the
 *  TanStack query key (via useNetworkDevices — never build a device query
 *  inline here).
 *
 *  Vocabulary rule: "Make static" / "Static" / "Dynamic". The word
 *  "reservation" must not appear anywhere on this surface.
 *
 *  Below 48em rows become cards (no MAC in the list); tapping one pushes
 *  ?device=<mac> and opens a bottom drawer with the full details and the
 *  Disconnect / Block actions — pushed, so the hardware back button closes
 *  the sheet instead of leaving the page. */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMediaQuery } from "@mantine/hooks";
import { Ban, Filter, Lock, MoreHorizontal, Pencil, Plus, Search, Trash2, Unplug } from "lucide-react";
import { toast } from "sonner";
import {
  Badge,
  Box,
  Button,
  Card,
  Drawer,
  Group,
  Indicator,
  Menu,
  Modal,
  Pagination,
  Popover,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { tenantApi } from "@/lib/api/client";
import type { DeviceActionResult, NetworkDevice } from "@/lib/api/client";
import { useAuthStore } from "@/lib/auth";
import { conventionalDhcpPool, errorStatus } from "@/components/network/shared";
import { AddStaticLeaseForm } from "@/components/network/add-static-lease-form";
import {
  MakeStaticModal,
  WhereCell,
  deviceName,
  deviceStatusDot,
  deviceStatusLabel,
  statusDot,
  useMakeStatic,
  whereText,
} from "@/components/network/device-parts";
import {
  NETWORK_DEVICES_QUERY_KEY,
  useNetworkDevices,
  useNetworkInfo,
  type DeviceListFilters,
} from "@/hooks/use-network-queries";

const SHOW_VALUES = ["online", "offline", "static", "blocked", "history"] as const;
type ShowValue = (typeof SHOW_VALUES)[number];

export default function NetworkDevicesTab() {
  const { user } = useAuthStore();
  const isAdmin = !!user?.is_tenant_admin;
  const isMobile = useMediaQuery("(max-width: 48em)");
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // The URL is the source of truth for every filter — shareable, and the ONE
  // params object below feeds both the request and the query key.
  const scope: DeviceListFilters["scope"] =
    isAdmin && searchParams.get("scope") === "everyone" ? "everyone" : "mine";
  const show = useMemo(
    () =>
      (searchParams.get("show") ?? "")
        .split(",")
        .filter((v): v is ShowValue => (SHOW_VALUES as readonly string[]).includes(v)),
    [searchParams],
  );
  const q = searchParams.get("q") ?? "";
  const page = Math.max(1, parseInt(searchParams.get("page") ?? "1", 10) || 1);
  const openMac = searchParams.get("device");

  const filters: DeviceListFilters = { scope, show, q, page };
  const devicesQuery = useNetworkDevices(filters);
  const netInfo = useNetworkInfo();

  // Filter changes REPLACE history (the account.tsx pattern) — flipping chips
  // must not bury the previous tab under twenty history entries. Every filter
  // change also resets pagination: page 3 of a different filter is nowhere.
  const updateFilters = (mutate: (next: URLSearchParams) => void) => {
    // Functional form, not `new URLSearchParams(searchParams)`. The search
    // debounce below fires from a closure captured up to 300ms ago, so building
    // from that render's params silently reverted any chip or scope the user
    // touched while typing.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  };

  const setScope = (value: DeviceListFilters["scope"]) =>
    updateFilters((next) => {
      if (value === "everyone") next.set("scope", "everyone");
      else next.delete("scope");
    });

  const toggleShow = (value: ShowValue) =>
    updateFilters((next) => {
      const current = new Set(show);
      if (current.has(value)) current.delete(value);
      else current.add(value);
      if (current.size) next.set("show", [...current].join(","));
      else next.delete("show");
    });

  const setPage = (value: number) => {
    const next = new URLSearchParams(searchParams);
    if (value > 1) next.set("page", String(value));
    else next.delete("page");
    setSearchParams(next, { replace: true });
  };

  // Search box: local echo of ?q= with a 300ms debounce so each keystroke
  // doesn't fetch, while the URL stays canonical.
  const [searchInput, setSearchInput] = useState(q);
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== q) {
        updateFilters((next) => {
          if (searchInput) next.set("q", searchInput);
          else next.delete("q");
        });
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  // ── Device sheet (?device=<mac>) ─────────────────────────────────────────
  // Opening PUSHES so the hardware back button closes the sheet rather than
  // leaving the page; closing strips the param.
  const openDevice = (mac: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("device", mac);
    setSearchParams(next);
  };
  const closeDevice = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("device");
    setSearchParams(next, { replace: true });
  };

  const devices = devicesQuery.data?.results ?? [];
  const openedDevice = openMac ? devices.find((d) => d.mac_address === openMac) ?? null : null;

  // A ?device= that no longer matches anything is a loaded gun: the drawer is
  // shut, but the next result set containing that MAC pops it open unbidden.
  // Only once the query has SETTLED — mid-fetch the list is empty and every
  // device would look gone.
  useEffect(() => {
    if (openMac && devicesQuery.isSuccess && !openedDevice) closeDevice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMac, devicesQuery.isSuccess, openedDevice]);

  // ── Static-lease actions (salvaged from the old leases page) ─────────────
  const invalidateDevices = () =>
    queryClient.invalidateQueries({ queryKey: NETWORK_DEVICES_QUERY_KEY });

  const [makeStaticFor, setMakeStaticFor] = useState<NetworkDevice | null>(null);
  const { making, makeStatic: handleMakeStatic } = useMakeStatic(() => setMakeStaticFor(null));
  const [removing, setRemoving] = useState<number | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingDevice, setEditingDevice] = useState<NetworkDevice | null>(null);
  const [editForm, setEditForm] = useState({ ip_address: "", mac_address: "", hostname: "", comment: "" });

  const handleRemoveStatic = async (id: number) => {
    setRemoving(id);
    try {
      await tenantApi.deleteStaticLease(id);
      toast.success("Address is dynamic again");
      invalidateDevices();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      toast.error(e.response?.data?.detail || "Failed to remove the static address");
    } finally {
      setRemoving(null);
    }
  };

  const handleEditStatic = (device: NetworkDevice) => {
    setEditingDevice(device);
    setEditForm({
      ip_address: device.static_ip_address || device.ip_address || "",
      mac_address: device.mac_address,
      hostname: device.hostname ?? "",
      comment: "",
    });
  };

  const handleSaveEdit = async () => {
    if (!editingDevice?.static_lease_id) return;
    try {
      // A device row carries no comment, so this form cannot prefill the
      // existing one — and the backend applies any comment it is given. Sending
      // the empty box to mean "unchanged" erased whoever wrote it, silently, on
      // an unrelated hostname edit. So the key is omitted unless something was
      // actually typed. The cost is that a comment cannot be cleared from here,
      // which is the right way round: losing one by accident is worse than
      // needing the leases admin to remove it deliberately.
      //
      // `hostname` needs the same care for a different reason: it IS prefilled,
      // but from what the network currently OBSERVES (DHCP/collector), not from
      // what the lease stores. Sending it back renames a lease from
      // "reception-printer" to "HP8022DN" as a side effect of correcting an
      // address, so it too only goes if the box actually changed.
      const { comment, hostname, ...rest } = editForm;
      const payload: Record<string, string> = { ...rest };
      if (comment.trim()) payload.comment = comment;
      if (hostname !== (editingDevice.hostname ?? "")) payload.hostname = hostname;
      await tenantApi.updateStaticLease(editingDevice.static_lease_id, payload);
      toast.success("Static address updated");
      setEditingDevice(null);
      invalidateDevices();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      toast.error(e.response?.data?.detail || "Failed to update the static address");
    }
  };

  // ── Disconnect / block (#310) ────────────────────────────────────────────
  const reportActionResult = (result: DeviceActionResult, successMessage: string) => {
    if (result.status === "failed") {
      toast.error(result.detail || "The action failed — nothing was changed");
    } else if (result.status === "partial") {
      // A partial block is a warning state, not a success: the RADIUS gate is
      // shut but the controller was not reached, so the device may still sit
      // on a PSK network while a plain "Blocked" would claim otherwise.
      toast.warning(result.detail || "Only partly applied — one enforcement layer could not be reached");
    } else {
      toast.success(result.detail || successMessage);
    }
    invalidateDevices();
  };

  const disconnectMutation = useMutation({
    mutationFn: (mac: string) => tenantApi.disconnectDevice(mac).then((r) => r.data),
    onSuccess: (result) => {
      reportActionResult(result, "Disconnected — a device with valid credentials can reconnect in seconds");
      setDisconnectFor(null);
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      toast.error(e.response?.data?.detail || "Failed to disconnect the device");
    },
  });

  const blockMutation = useMutation({
    mutationFn: ({ mac, label }: { mac: string; label: string }) =>
      tenantApi.blockDevice(mac, label).then((r) => r.data),
    onSuccess: (result) => {
      reportActionResult(result, "Blocked site-wide");
      setBlockFor(null);
      setBlockLabel("");
    },
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      toast.error(e.response?.data?.detail || "Failed to block the device");
    },
  });

  const unblockMutation = useMutation({
    mutationFn: (mac: string) => tenantApi.unblockDevice(mac).then((r) => r.data),
    onSuccess: (result) => reportActionResult(result, "Unblocked — the device can join again"),
    onError: (err: unknown) => {
      const e = err as { response?: { data?: { detail?: string } } };
      toast.error(e.response?.data?.detail || "Failed to unblock the device");
    },
  });

  const [disconnectFor, setDisconnectFor] = useState<NetworkDevice | null>(null);
  const [blockFor, setBlockFor] = useState<NetworkDevice | null>(null);
  const [blockLabel, setBlockLabel] = useState("");

  // Disconnect and block: admins on their tenant's devices, members on their
  // own. Static leases are a DIFFERENT gate — create/update/delete are all hard
  // "Tenant admin required" server-side, so showing them to a member renders a
  // button whose only possible outcome is an error toast.
  const canAct = (device: NetworkDevice) => isAdmin || device.is_mine;
  const canManageStatic = isAdmin;

  // ── Rendering ────────────────────────────────────────────────────────────
  const notAvailable = errorStatus(devicesQuery.error) === 404;
  const forbidden = errorStatus(devicesQuery.error) === 403;
  const hardError = devicesQuery.isError && !notAvailable && !forbidden;

  const pool = netInfo.data?.prefix ? conventionalDhcpPool(netInfo.data.prefix) : null;

  const scopeChips = (
    <Group gap={4}>
      <Button variant={scope === "mine" ? "filled" : "outline"} size="xs" onClick={() => setScope("mine")}>
        Mine
      </Button>
      {isAdmin && (
        <Button
          variant={scope === "everyone" ? "filled" : "outline"}
          size="xs"
          onClick={() => setScope("everyone")}
        >
          Everyone
        </Button>
      )}
    </Group>
  );

  const showChip = (value: ShowValue, label: string, icon?: ReactNode) => (
    <Button
      key={value}
      variant={show.includes(value) ? "filled" : "outline"}
      size="xs"
      onClick={() => toggleShow(value)}
      leftSection={icon}
    >
      {label}
    </Button>
  );

  const showChips = (
    <>
      {showChip("online", "Online")}
      {showChip("offline", "Offline")}
      {showChip("static", "Static", <Lock size={12} />)}
      {showChip("blocked", "Blocked", <Ban size={12} />)}
      {showChip("history", "Show history")}
    </>
  );

  const ipCell = (device: NetworkDevice, compact = false) => {
    const busy = making === device.mac_address;
    const address = device.ip_address || device.static_ip_address;
    if (!address) return <Text c="dimmed" component="span">-</Text>;

    if (busy || device.lease_state === "making_static") {
      return (
        <Group gap={6} wrap="nowrap">
          <Text ff="monospace" fz="sm" component="span">{address}</Text>
          {statusDot("var(--mantine-color-yellow-5)", true)}
          <Text fz="xs" c="dimmed" component="span">Making static…</Text>
        </Group>
      );
    }
    if (device.lease_state === "removing") {
      return (
        <Group gap={6} wrap="nowrap">
          <Text ff="monospace" fz="sm" component="span">{address}</Text>
          {statusDot("var(--mantine-color-red-5)", true)}
          <Text fz="xs" c="dimmed" component="span">Removing…</Text>
        </Group>
      );
    }
    if (device.lease_state === "static") {
      const staleAddress =
        device.static_ip_address && device.ip_address && device.static_ip_address !== device.ip_address;
      const cell = (
        <Group gap={6} wrap="nowrap">
          <Text ff="monospace" fz="sm" component="span">{device.static_ip_address || address}</Text>
          <Badge variant="outline" size="xs">Static</Badge>
          {staleAddress && (
            <Text fz="xs" c="dimmed" component="span" title="The device is still on its old dynamic address">
              now {device.ip_address}
            </Text>
          )}
        </Group>
      );
      if (compact || !canManageStatic || !device.static_lease_id) return cell;
      // The address's own state and its actions live together on the cell.
      return (
        <Menu position="bottom-start">
          <Menu.Target>
            <Box style={{ cursor: "pointer" }}>{cell}</Box>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item leftSection={<Pencil size={16} />} onClick={() => handleEditStatic(device)}>
              Edit static address
            </Menu.Item>
            <Menu.Item
              leftSection={<Trash2 size={16} />}
              color="red"
              disabled={removing === device.static_lease_id}
              onClick={() => handleRemoveStatic(device.static_lease_id!)}
            >
              Make dynamic again
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      );
    }
    // Plain dynamic address.
    return (
      <Group gap={6} wrap="nowrap">
        <Text ff="monospace" fz="sm" component="span">{address}</Text>
        <Text fz="xs" c="dimmed" component="span">Dynamic</Text>
        {!compact && canManageStatic && (
          <Button variant="subtle" size="compact-xs" onClick={() => setMakeStaticFor(device)}>
            Make static
          </Button>
        )}
      </Group>
    );
  };

  const actionMenu = (device: NetworkDevice) =>
    canAct(device) ? (
      <Menu position="bottom-end">
        <Menu.Target>
          <Button variant="subtle" size="compact-sm" aria-label="Device actions">
            <MoreHorizontal size={16} />
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Item
            leftSection={<Unplug size={16} />}
            disabled={device.status !== "online"}
            onClick={() => setDisconnectFor(device)}
          >
            Disconnect
          </Menu.Item>
          {device.blocked ? (
            <Menu.Item
              leftSection={<Ban size={16} />}
              disabled={unblockMutation.isPending}
              onClick={() => unblockMutation.mutate(device.mac_address)}
            >
              Unblock
            </Menu.Item>
          ) : (
            <Menu.Item leftSection={<Ban size={16} />} color="red" onClick={() => setBlockFor(device)}>
              Block…
            </Menu.Item>
          )}
        </Menu.Dropdown>
      </Menu>
    ) : null;

  const deviceCell = (device: NetworkDevice, withMac: boolean) => (
    <Box>
      <Group gap={6} wrap="nowrap">
        <Text fz="sm" fw={500} truncate>{deviceName(device)}</Text>
        {device.is_mine && <Badge variant="light" size="xs">Yours</Badge>}
        {device.blocked && <Badge color="red" variant="light" size="xs">Blocked</Badge>}
      </Group>
      {withMac && (
        <Text fz="xs" c="dimmed" ff="monospace">
          {device.mac_address}
          {device.vendor && device.hostname ? ` · ${device.vendor}` : ""}
        </Text>
      )}
    </Box>
  );

  const emptyState = notAvailable ? (
    <Text fz="sm" c="dimmed" p="md" ta="center">
      The device list isn't available on your site yet.
    </Text>
  ) : forbidden ? (
    <Text fz="sm" c="dimmed" p="md" ta="center">
      You don't have access to other people's devices.
    </Text>
  ) : hardError ? (
    <Text fz="sm" c="red" p="md">Couldn't load your devices. Please try again.</Text>
  ) : devicesQuery.isLoading ? (
    <Text fz="sm" c="dimmed" p="md">Loading...</Text>
  ) : devices.length === 0 ? (
    <Text fz="sm" c="dimmed" p="md" ta="center">
      {q || show.length ? "No devices match these filters." : "No devices seen on your network yet."}
    </Text>
  ) : null;

  const totalPages = devicesQuery.data?.total_pages ?? 1;

  return (
    <Stack gap="md">
      <Group gap="sm" wrap="wrap">
        <TextInput
          placeholder="Search name, MAC, or IP..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          leftSection={<Search size={16} />}
          style={{ flex: 1, minWidth: 180, maxWidth: 384 }}
        />
        {scopeChips}
        {isMobile ? (
          <Indicator disabled={show.length === 0} label={show.length} size={16}>
            <Popover position="bottom-end" withArrow>
              <Popover.Target>
                <Button variant="outline" size="xs" leftSection={<Filter size={12} />}>
                  Filters
                </Button>
              </Popover.Target>
              <Popover.Dropdown>
                <Stack gap={4} align="stretch">{showChips}</Stack>
              </Popover.Dropdown>
            </Popover>
          </Indicator>
        ) : (
          <Group gap={4}>{showChips}</Group>
        )}
        {isAdmin && !isMobile && (
          <Button size="xs" variant="default" onClick={() => setShowAddDialog(true)} leftSection={<Plus size={14} />}>
            Add static address
          </Button>
        )}
      </Group>

      <Card withBorder padding={0}>
        {emptyState ??
          (isMobile ? (
            <Stack gap={0}>
              {devices.map((device, i) => (
                <Box
                  key={device.mac_address}
                  p="sm"
                  onClick={() => openDevice(device.mac_address)}
                  style={{
                    cursor: "pointer",
                    borderTop: i > 0 ? "1px solid var(--mantine-color-default-border)" : undefined,
                    background: device.blocked ? "var(--mantine-color-red-light)" : undefined,
                  }}
                >
                  <Group gap="sm" wrap="nowrap">
                    {deviceStatusDot(device)}
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      {/* No MAC in the mobile list — it lives in the sheet. */}
                      <Group gap={6} wrap="nowrap">
                        <Text fz="sm" fw={500} truncate>{deviceName(device)}</Text>
                        {device.is_mine && <Badge variant="light" size="xs">Yours</Badge>}
                        {device.blocked && <Badge color="red" variant="light" size="xs">Blocked</Badge>}
                      </Group>
                      <Text fz="xs" c="dimmed" truncate>
                        {[whereText(device), device.ip_address].filter(Boolean).join(" · ") ||
                          deviceStatusLabel(device)}
                      </Text>
                    </Box>
                  </Group>
                </Box>
              ))}
            </Stack>
          ) : (
            <Box style={{ overflowX: "auto" }}>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Device</Table.Th>
                    {isAdmin && <Table.Th>Signed in as</Table.Th>}
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Where</Table.Th>
                    <Table.Th>IP</Table.Th>
                    <Table.Th w={48} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {devices.map((device) => (
                    <Table.Tr
                      key={device.mac_address}
                      style={device.blocked ? { background: "var(--mantine-color-red-light)" } : undefined}
                    >
                      <Table.Td>{deviceCell(device, true)}</Table.Td>
                      {isAdmin && (
                        <Table.Td>
                          {device.signed_in_as ? (
                            <Text fz="sm">{device.signed_in_as}</Text>
                          ) : (
                            <Text c="dimmed" component="span">-</Text>
                          )}
                        </Table.Td>
                      )}
                      <Table.Td>
                        <Group gap={6} wrap="nowrap">
                          {deviceStatusDot(device)}
                          <Text fz="sm" component="span">{deviceStatusLabel(device)}</Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <WhereCell device={device} />
                      </Table.Td>
                      <Table.Td>{ipCell(device)}</Table.Td>
                      <Table.Td>{actionMenu(device)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Box>
          ))}
      </Card>

      {totalPages > 1 && (
        <Group justify="center">
          <Pagination value={page} total={totalPages} onChange={setPage} size="sm" />
        </Group>
      )}

      <Text fz="xs" c="dimmed" ta="center">
        Static address changes are applied to the router within 3 minutes
      </Text>

      {/* ── Bottom sheet: full details for one device (?device=<mac>) ────── */}
      <Drawer
        opened={!!openedDevice}
        onClose={closeDevice}
        position="bottom"
        title={openedDevice ? deviceName(openedDevice) : ""}
        size="auto"
      >
        {openedDevice && (
          <Stack gap="md" pb="md">
            <Stack gap={6}>
              <Group justify="space-between">
                <Text fz="sm" c="dimmed">MAC address</Text>
                <Text fz="sm" ff="monospace">{openedDevice.mac_address}</Text>
              </Group>
              {openedDevice.vendor && (
                <Group justify="space-between">
                  <Text fz="sm" c="dimmed">Manufacturer</Text>
                  <Text fz="sm">{openedDevice.vendor}</Text>
                </Group>
              )}
              <Group justify="space-between">
                <Text fz="sm" c="dimmed">Status</Text>
                <Group gap={6}>
                  {deviceStatusDot(openedDevice)}
                  <Text fz="sm">{deviceStatusLabel(openedDevice)}</Text>
                </Group>
              </Group>
              {whereText(openedDevice) && (
                <Group justify="space-between">
                  <Text fz="sm" c="dimmed">Where</Text>
                  <WhereCell device={openedDevice} />
                </Group>
              )}
              <Group justify="space-between">
                <Text fz="sm" c="dimmed">IP</Text>
                {ipCell(openedDevice, true)}
              </Group>
              {isAdmin && openedDevice.signed_in_as && (
                <Group justify="space-between">
                  <Text fz="sm" c="dimmed">Signed in as</Text>
                  <Text fz="sm">{openedDevice.signed_in_as}</Text>
                </Group>
              )}
            </Stack>

            {canAct(openedDevice) && (
              <>
                {canManageStatic && openedDevice.lease_state == null && openedDevice.ip_address && (
                  <Button variant="default" onClick={() => setMakeStaticFor(openedDevice)}>
                    Make static
                  </Button>
                )}
                {/* A phone is where a lost device gets dealt with, so the static
                    controls belong here too — the desktop table hides them
                    behind a row menu that has no equivalent on a card. */}
                {canManageStatic && openedDevice.static_lease_id != null && (
                  <>
                    <Button variant="default" onClick={() => { handleEditStatic(openedDevice); closeDevice(); }}>
                      Edit static address
                    </Button>
                    <Button
                      variant="default"
                      color="red"
                      loading={removing === openedDevice.static_lease_id}
                      onClick={async () => {
                        await handleRemoveStatic(openedDevice.static_lease_id as number);
                        closeDevice();
                      }}
                    >
                      Make dynamic again
                    </Button>
                  </>
                )}
                <Button
                  variant="default"
                  leftSection={<Unplug size={16} />}
                  disabled={openedDevice.status !== "online"}
                  onClick={() => setDisconnectFor(openedDevice)}
                >
                  Disconnect
                </Button>
                {openedDevice.blocked ? (
                  <Button
                    variant="default"
                    leftSection={<Ban size={16} />}
                    loading={unblockMutation.isPending}
                    onClick={() => unblockMutation.mutate(openedDevice.mac_address)}
                  >
                    Unblock
                  </Button>
                ) : (
                  <Button color="red" variant="light" leftSection={<Ban size={16} />} onClick={() => setBlockFor(openedDevice)}>
                    Block…
                  </Button>
                )}
                <Text fz="xs" c="dimmed">
                  Disconnecting is temporary — a device with valid credentials reconnects within
                  seconds. Blocking keeps the device off every network at the site, including guest
                  Wi-Fi, until you unblock it.
                </Text>
              </>
            )}
          </Stack>
        )}
      </Drawer>

      {/* ── Disconnect confirmation ──────────────────────────────────────── */}
      <Modal opened={!!disconnectFor} onClose={() => setDisconnectFor(null)} title="Disconnect device">
        {disconnectFor && (
          <Stack gap="md">
            <Text fz="sm">
              Disconnect <Text component="span" fw={600}>{deviceName(disconnectFor)}</Text> from the
              network now?
            </Text>
            <Text fz="sm" c="dimmed">
              A disconnect is temporary: a device with valid credentials reconnects on its own within
              seconds. To keep it off the network, block it instead.
            </Text>
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={() => setDisconnectFor(null)}>Cancel</Button>
              <Button
                loading={disconnectMutation.isPending}
                onClick={() => disconnectMutation.mutate(disconnectFor.mac_address)}
              >
                Disconnect
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* ── Block confirmation — honest about its reach ──────────────────── */}
      <Modal opened={!!blockFor} onClose={() => setBlockFor(null)} title="Block device">
        {blockFor && (
          <Stack gap="md">
            <Text fz="sm">
              Block <Text component="span" fw={600}>{deviceName(blockFor)}</Text>?
            </Text>
            <Text fz="sm" c="dimmed">
              A block is site-wide: it keeps this device off every network at the site — including
              guest Wi-Fi — disconnects it now, and stays in force until you unblock it. It does not
              sign the person out; their account still works on their other devices.
            </Text>
            <TextInput
              label="Reason (optional)"
              placeholder="e.g. Lost laptop"
              value={blockLabel}
              onChange={(e) => setBlockLabel(e.target.value)}
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="default" onClick={() => setBlockFor(null)}>Cancel</Button>
              <Button
                color="red"
                loading={blockMutation.isPending}
                onClick={() => blockMutation.mutate({ mac: blockFor.mac_address, label: blockLabel })}
              >
                Block device
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* ── Make static: keep this address or choose another ─────────────── */}
      <MakeStaticModal
        device={makeStaticFor}
        pool={pool}
        busy={!!makeStaticFor && making === makeStaticFor.mac_address}
        onClose={() => setMakeStaticFor(null)}
        onSubmit={handleMakeStatic}
      />

      {/* ── Add a static address by hand (admin) — salvaged from the old
             leases page, for a device that is not in the list yet ─────────── */}
      <Modal opened={showAddDialog} onClose={() => setShowAddDialog(false)} title="Add static address">
        <AddStaticLeaseForm pool={pool} onAdded={() => setShowAddDialog(false)} />
      </Modal>

      {/* ── Edit static address — salvaged from the old leases page ──────── */}
      <Modal opened={!!editingDevice} onClose={() => setEditingDevice(null)} title="Edit static address">
        <Stack gap="sm">
          <TextInput
            placeholder="IP Address"
            value={editForm.ip_address}
            onChange={(e) => setEditForm((p) => ({ ...p, ip_address: e.target.value }))}
          />
          <TextInput
            placeholder="MAC Address"
            value={editForm.mac_address}
            onChange={(e) => setEditForm((p) => ({ ...p, mac_address: e.target.value }))}
          />
          <TextInput
            placeholder="Hostname (optional)"
            value={editForm.hostname}
            onChange={(e) => setEditForm((p) => ({ ...p, hostname: e.target.value }))}
          />
          <TextInput
            placeholder="Add a comment (blank keeps the existing one)"
            value={editForm.comment}
            onChange={(e) => setEditForm((p) => ({ ...p, comment: e.target.value }))}
          />
          <Button fullWidth onClick={handleSaveEdit} disabled={!editForm.ip_address || !editForm.mac_address}>
            Save Changes
          </Button>
        </Stack>
      </Modal>
    </Stack>
  );
}
